import { ApolloClient, ApolloLink, gql, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import * as prismic from '@prismicio/client';
import { useMemo } from 'react';
import { prismicConfic } from './config';
import { PrismicHttpLink } from './prismicLink';

const GRAPHQL_API_URL = `https://${prismicConfic.REPOSITORY}.prismic.io/graphql`;
const REF_API_URL = `https://${prismicConfic.REPOSITORY}.cdn.prismic.io/api/v2`;

if (typeof window === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { bootstrap: bootstrapGlobalAgent } = require('global-agent');
  // Setup global support for environment variable based proxy configuration.
  bootstrapGlobalAgent();
}

interface GetApolloClientProps {
  initialState?: any;
  prismicRef?: string | null;
  introspectionQueryResultData?: any;
}

export const PrismicClient = prismic.createClient(REF_API_URL, {
  accessToken: prismicConfic.API_TOKEN,
});

/**
 * Here we can do third-party data-fetching and re-use the
 * apollo cache for client rehydration.
 */
export const typeDefs = gql`
  extend type Query {
    articlesByCategories(categories: [ID!]): [Article]
  }
`;

export let apolloClient: ApolloClient<any>;

function createApolloClient(
  introspectionResultData: any,
  { prismicRef = null } = {},
) {
  // force the preview reference as prismic-ref
  const previewLink = setContext((request, previousContext) => ({
    headers: {
      ...previousContext.headers,
      ...(prismicRef ? { 'Prismic-ref': prismicRef } : {}),
    },
  }));

  const cache = new InMemoryCache({
    possibleTypes: introspectionResultData,
    typePolicies: {
      Page: {
        keyFields: ['_meta', ['uid']],
        fields: {
          body: {
            merge(existing: any[], incoming: any[], { mergeObjects }) {
              if (!existing) {
                return incoming;
              }
              return existing.map((x, index) =>
                mergeObjects(x, incoming[index]),
              );
            },
          },
        },
      },
    },
  });

  // setup prismic link
  const prismicLink = PrismicHttpLink({
    uri: GRAPHQL_API_URL,
    accessToken: prismicConfic.API_TOKEN,
    repositoryName: prismicConfic.REPOSITORY,
  });

  const logLink = new ApolloLink((operation, forward) => {
    if (process.env.DEBUG === 'apollo') {
      console.time(operation.operationName);
    }
    return forward(operation).map((result) => {
      if (process.env.DEBUG === 'apollo') {
        console.timeEnd(operation.operationName);
      }
      return result;
    });
  });

  // setup apollo client
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([logLink, previewLink.concat(prismicLink)]),
    cache,
  });
}

export function getApolloClient({
  introspectionQueryResultData,
  initialState = null,
  prismicRef = null,
}: GetApolloClientProps = {}) {
  const _apolloClient =
    apolloClient ??
    createApolloClient(introspectionQueryResultData, {
      prismicRef: prismicRef as any,
    });

  // hydrate apollo cache
  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }

  // for SSG and SSR always create a new apollo client
  if (typeof window === 'undefined') {
    return _apolloClient;
  }

  // create the apollo client once in the client
  if (!apolloClient) {
    apolloClient = _apolloClient;
  }

  return _apolloClient;
}

export function useApollo({
  initialState,
  prismicRef,
  introspectionQueryResultData,
}: GetApolloClientProps) {
  // memoize the apollo client for the rest of the react render lifecycle
  return useMemo(
    () =>
      getApolloClient({
        introspectionQueryResultData,
        initialState,
        prismicRef,
      }),
    [initialState, prismicRef, introspectionQueryResultData],
  );
}
